/**
 * File-Based Rule Types - single source of truth for the wizard's rule-type list
 *
 * Both the rule-type dropdown (RuleBuilder.tsx) and the step-2 help panel
 * (RuleBuilderStep.tsx) render from this list, so the two can never drift out
 * of sync again (#1663 — the help panel had documented 7 of the 8 dropdown
 * options, omitting `dataset_name`, which #1631 measured as the most-used
 * rule type in production).
 *
 * `view_name` is intentionally excluded: it is the Build-From-View source
 * type, not a file-based rule, and is not offered in this dropdown.
 *
 * Exhaustiveness: `FILE_BASED_RULE_TYPE_INFO` is typed as
 * `Record<FileBasedRuleType, ...>`, which requires an entry for every member
 * of the `FileBasedRuleType` union and rejects unknown keys. If `RuleType`
 * ever gains a 9th (non-`view_name`) member, this file fails to compile
 * until that member is added here — the shared source itself cannot silently
 * fall behind the type the way a plain array could.
 */

import type { RuleType } from '@/types/views';

export type FileBasedRuleType = Exclude<RuleType, 'view_name'>;

export interface FileBasedRuleTypeOption {
  value: FileBasedRuleType;
  label: string;
  description: string;
}

/**
 * Display order comes from this object's own key order (`Object.keys` walks
 * string keys in insertion order per the ECMAScript spec, and none of these
 * keys are integer-like). Deliberately not a second, separately-ordered
 * array: a parallel list of keys would itself be a second place to keep in
 * sync and would reintroduce exactly the kind of drift this file exists to
 * prevent.
 */
const FILE_BASED_RULE_TYPE_INFO: Record<FileBasedRuleType, Omit<FileBasedRuleTypeOption, 'value'>> = {
  path_pattern: {
    label: 'Path Pattern',
    description: 'Match files by glob patterns (e.g., /data/**/*.parquet)',
  },
  source_id: {
    label: 'Source ID',
    description: 'Include files from specific data sources',
  },
  dataset_id: {
    label: 'Dataset ID',
    description: 'Include files from specific datasets',
  },
  dataset_name: {
    label: 'Dataset Name',
    description: 'Include files from datasets matching a name',
  },
  metadata: {
    label: 'Metadata',
    description: 'Filter by file metadata fields',
  },
  date_range: {
    label: 'Date Range',
    description: 'Filter files by date fields',
  },
  file_size: {
    label: 'File Size',
    description: 'Filter files by size constraints',
  },
  tag: {
    label: 'Tag',
    description: 'Match files by tags',
  },
};

export const FILE_BASED_RULE_TYPES: FileBasedRuleTypeOption[] = (
  Object.keys(FILE_BASED_RULE_TYPE_INFO) as FileBasedRuleType[]
).map((value) => ({ value, ...FILE_BASED_RULE_TYPE_INFO[value] }));
